import { useState, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useCompany } from '../../context/CompanyContext';
import { logUserActivity } from '../../lib/activityLogger';


/* ─────────────────────────────────────────────────────────────────────────
 * Lattice Conveyor Calculator — Web port of VBA FormLattice
 * (Lattice Calculator Program2.xlsm)
 *
 * Handles: Lattice conveyor design, component selection,
 *          dimensional calculations, and pricing.
 * ───────────────────────────────────────────────────────────────────────── */

// ─── Option Enums ───

const LAG_MATERIALS = [
  { code: 'Aluminium', label: 'Aluminium Lags' },
  { code: 'Rigid PVC Section', label: 'Rigid PVC Section' },
  { code: 'Mild Steel', label: 'Mild Steel' },
];

const ALUMINIUM_SHAPES = ['Pentagon', 'Hexagon'];
const PVC_SHAPES = ['Solid', 'Semi Solid'];

const BELT_TYPES = [
  { code: 'LL', label: 'LL — Link Laced' },
  { code: 'LF', label: 'LF — Laced Flat' },
  { code: 'AR', label: 'AR — Arrow' },
  { code: 'Unknown', label: 'Unknown' },
];

const BELT_WIDTHS = ['15mm', '20mm', '25mm', '32mm', '40mm'];

const NUMBER_OF_BELTS_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

const JOINING_METHODS = [
  { code: 'End Weld', label: 'End Weld' },
  { code: 'Overlap', label: 'Overlap Joint' },
  { code: 'Metal Hinge', label: 'Metal Hinge' },
];

const HINGE_TYPES = ['Type A', 'Type B', 'Type C'];

const PIN_ANGLES = ['Straight', '20°', '30°', '45°', '60°', '90°'];

const BACK_CHANNEL_TYPES = [
  { code: 'bcf', label: 'BCF — Back Channel Flat' },
  { code: 'bwf', label: 'BWF — Back Channel Welded Flat' },
  { code: 'pa', label: 'PA — Plain Angle' },
  { code: 'pw', label: 'PW — Plain Welded' },
];

const SPOKE_LIST = ['Type 1', 'Type 2', 'Type 3', 'Custom'];

const RIVET_SIZES = ['M4', 'M5', 'M6', 'M8', '1/8"', '5/32"'];

interface LatticeForm {
  // Basic
  lagMaterial: string;
  lagShape: string;
  lagSize: string;
  latticeLength: number | '';
  latticeWidth: number | '';
  quantity: number | '';
  dia1: number | '';
  dia2: number | '';
  centreDistance: number | '';
  lagDistance: number | '';

  // Frame
  numberOfBoltsNarrow: number | '';
  numberOfBoltsWide: number | '';
  numberOfFlatPlates: number | '';
  flap: number | '';
  otherSpec: string;

  // Pin / Joining
  pinAngle: string;
  joiningMethod: string;
  hingeType: string;

  // Lag Dimensions
  lagHeight: number | '';
  lagWidth: number | '';
  numberOfLags: number | '';

  // Calculated
  effectiveLength: number;

  // Optional sections
  enableBelt: boolean;
  enableAP: boolean;
  enableBAC: boolean;
  enableSpokes: boolean;
  enableRivets: boolean;

  // Belt details
  beltType: string;
  beltPattern: string;
  distanceBetweenSupports: number | '';
  numberOfBelts: string;
  beltWidth: string;
  beltWidth2: string;
  numberOfBelts2: string;
  numberOfBeltsRemaining: string;
  beltSpacing: number | '';
  beltSpacing2: number | '';

  // Adjustable Position
  apPosition: string;
  apThickness: string;

  // Back Channel
  backChannelType: string;
  strapSupport: boolean;

  // Spokes
  spokeList: string;

  // Rivets
  rivetSize: string;

  // I/C or O/C
  insideClearance: boolean;
  outsideClearance: boolean;

  // Company info
  companyId: string;
  repId: string;
  partyId: string;
}

const INITIAL_FORM: LatticeForm = {
  lagMaterial: '', lagShape: '', lagSize: '',
  latticeLength: '', latticeWidth: '', quantity: '',
  dia1: '', dia2: '', centreDistance: '', lagDistance: '',
  numberOfBoltsNarrow: '', numberOfBoltsWide: '', numberOfFlatPlates: '',
  flap: '', otherSpec: '',
  pinAngle: '', joiningMethod: '', hingeType: '',
  lagHeight: '', lagWidth: '', numberOfLags: '',
  effectiveLength: 0,
  enableBelt: false, enableAP: false, enableBAC: false, enableSpokes: false, enableRivets: false,
  beltType: '', beltPattern: '', distanceBetweenSupports: '', numberOfBelts: '',
  beltWidth: '', beltWidth2: '', numberOfBelts2: '', numberOfBeltsRemaining: '',
  beltSpacing: '', beltSpacing2: '',
  apPosition: '', apThickness: '',
  backChannelType: '', strapSupport: false,
  spokeList: '', rivetSize: '',
  insideClearance: false, outsideClearance: false,
  companyId: '', repId: '', partyId: '',
};

export default function LatticeCalculatorPage() {
  const { companies } = useCompany();
  const [form, setForm] = useState<LatticeForm>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // ─── Effective Length Calculation (replicates VBA UpdateTBLength) ───
  const effectiveLength = useMemo(() => {
    const latLength = Number(form.latticeLength) || 0;
    const lh = Number(form.lagHeight) || 0;
    const value1 = Math.ceil(lh * 8.33);
    if (form.insideClearance) return latLength + value1;
    if (form.outsideClearance) return latLength - value1;
    return latLength;
  }, [form.latticeLength, form.lagHeight, form.insideClearance, form.outsideClearance]);

  // ─── Lattice Data Preview ───
  const latticeData = useMemo(() => {
    return [
      { label: 'Material of Lags', value: form.lagMaterial },
      { label: 'Lag Shape / Size', value: form.lagShape && form.lagSize ? `${form.lagShape} — ${form.lagSize}` : form.lagShape || '—' },
      { label: 'Lattice Length (mm)', value: form.latticeLength || '—' },
      { label: 'Effective Length (mm)', value: effectiveLength || '—' },
      { label: 'Lattice Width (mm)', value: form.latticeWidth || '—' },
      { label: 'Quantity', value: form.quantity || '—' },
      { label: 'Pin Dia 1 (mm)', value: form.dia1 || '—' },
      { label: 'Pin Dia 2 (mm)', value: form.dia2 || '—' },
      { label: 'Centre Distance (mm)', value: form.centreDistance || '—' },
      { label: 'Lag Distance (mm)', value: form.lagDistance || '—' },
      { label: 'No. of Bolts (Narrow)', value: form.numberOfBoltsNarrow || '—' },
      { label: 'No. of Bolts (Wide)', value: form.numberOfBoltsWide || '—' },
      { label: 'No. of Flat Plates', value: form.numberOfFlatPlates || '—' },
      { label: 'Flap (mm)', value: form.flap || '—' },
      { label: 'Pin Angle', value: form.pinAngle || '—' },
      { label: 'Joining Method', value: form.joiningMethod || '—' },
      { label: 'Lag Height (mm)', value: form.lagHeight || '—' },
      { label: 'Lag Width (mm)', value: form.lagWidth || '—' },
      { label: 'Number of Lags', value: form.numberOfLags || '—' },
    ].filter(d => d.value && d.value !== '—');
  }, [form, effectiveLength]);

  function handleChange(field: keyof LatticeForm, value: any) {
    setForm(prev => ({ ...prev, [field]: value }));
    // Reset lag shape/size when material changes
    if (field === 'lagMaterial') {
      setForm(prev => ({ ...prev, lagMaterial: value, lagShape: '', lagSize: '' }));
    }
  }

  async function handleSave() {
    if (!form.quantity) {
      alert('Please fill in Quantity.');
      return;
    }
    setSaving(true);
    try {
      // Save to a lattice_calculations table (or quotation_items)
      const data = {
        form_data: form,
        effective_length: effectiveLength,
        created_at: new Date().toISOString(),
        created_by: 'Administrator',
      };
      const quoteNum = `LAT-${Date.now()}`;
      await supabase.from('quotations').insert({
        quotation_number: quoteNum,
        quotation_date: new Date().toISOString().split('T')[0],
        company_id: form.companyId ? parseInt(form.companyId) : 1,
        rep_id: form.repId ? parseInt(form.repId) : 1,
        party_id: form.partyId ? parseInt(form.partyId) : 1,
        status: 'draft',
        remarks: JSON.stringify(data),
        subtotal: 0,
        total: 0,
        amount_in_words: 'Lattice Conveyor Calculation',
      });
      logUserActivity({
        action_type: 'QUOTATION_CREATED',
        resource_id: quoteNum,
        details: 'Created quotation from Lattice Conveyor Calculator',
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setForm(INITIAL_FORM);
  }

  const lagShapeOptions = form.lagMaterial === 'Aluminium'
    ? ALUMINIUM_SHAPES
    : form.lagMaterial === 'Rigid PVC Section'
    ? PVC_SHAPES
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-blue-900 rounded-2xl shadow-elevated p-6 sm:p-8 text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-blue-300 uppercase tracking-wider mb-1">
            <span>🏗️</span>
            <span>Lattice Conveyor Calculator</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Lattice Design & Pricing
          </h1>
          <p className="text-slate-300 text-sm mt-1">
            VBA FormLattice — Ported from Lattice Calculator Program2
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleReset} className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-xl border border-white/20 transition-colors cursor-pointer">
            Reset
          </button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-md shadow-blue-600/30 disabled:opacity-50 transition-all cursor-pointer">
            {saving ? 'Saving...' : '💾 Save Calculation'}
          </button>
        </div>
      </div>

      {saved && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl text-xs font-bold flex items-center gap-2">
          ✓ Lattice calculation saved successfully!
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ─── LEFT: Input Form ─── */}
        <div className="lg:col-span-2 space-y-6">

          {/* ─── SECTION 1: Lattice Dimensions ─── */}
          <SectionCard title="📐 Lattice Dimensions" step={1}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label>Material of Lags *</Label>
                <select value={form.lagMaterial} onChange={e => handleChange('lagMaterial', e.target.value)} className={inputClass}>
                  <option value="">Select</option>
                  {LAG_MATERIALS.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
                </select>
              </div>
              {lagShapeOptions.length > 0 && (
                <>
                  <div>
                    <Label>Lag Shape *</Label>
                    <select value={form.lagShape} onChange={e => handleChange('lagShape', e.target.value)} className={inputClass}>
                      <option value="">Select</option>
                      {lagShapeOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Lag Size</Label>
                    <select value={form.lagSize} onChange={e => handleChange('lagSize', e.target.value)} className={inputClass}>
                      <option value="">Select</option>
                      {form.lagMaterial === 'Aluminium' && (
                        <>
                          <option value="Aluminium_List_P">Pentagon List</option>
                          <option value="Aluminium_List_H">Hexagon List</option>
                        </>
                      )}
                      {form.lagMaterial === 'Rigid PVC Section' && (
                        <>
                          <option value="Poly_Lags_Solid">Solid PVC</option>
                          <option value="Poly_Lags_Semi">Semi Solid PVC</option>
                        </>
                      )}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              <Input label="Lattice Length (mm)" value={form.latticeLength} onChange={v => handleChange('latticeLength', v)} type="number" />
              <Input label="Width (mm)" value={form.latticeWidth} onChange={v => handleChange('latticeWidth', v)} type="number" />
              <Input label="Quantity *" value={form.quantity} onChange={v => handleChange('quantity', v)} type="number" required />
              <Input label="Centre Distance (mm)" value={form.centreDistance} onChange={v => handleChange('centreDistance', v)} type="number" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              <Input label="Pin Dia 1 (mm)" value={form.dia1} onChange={v => handleChange('dia1', v)} type="number" />
              <Input label="Pin Dia 2 (mm)" value={form.dia2} onChange={v => handleChange('dia2', v)} type="number" />
              <Input label="Lag Distance (mm)" value={form.lagDistance} onChange={v => handleChange('lagDistance', v)} type="number" />
              <div>
                <Label>I/C or O/C *</Label>
                <div className="flex gap-2">
                  <button onClick={() => { handleChange('insideClearance', true); handleChange('outsideClearance', false); }}
                    className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all cursor-pointer ${form.insideClearance ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-blue-300'}`}>
                    I/C (Inside)
                  </button>
                  <button onClick={() => { handleChange('outsideClearance', true); handleChange('insideClearance', false); }}
                    className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all cursor-pointer ${form.outsideClearance ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-blue-300'}`}>
                    O/C (Outside)
                  </button>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ─── SECTION 2: Frame / Channel ─── */}
          <SectionCard title="🔩 Frame / Channel" step={2}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Input label="No. Bolts (Narrow)" value={form.numberOfBoltsNarrow} onChange={v => handleChange('numberOfBoltsNarrow', v)} type="number" />
              <Input label="No. Bolts (Wide)" value={form.numberOfBoltsWide} onChange={v => handleChange('numberOfBoltsWide', v)} type="number" />
              <Input label="No. Flat Plates" value={form.numberOfFlatPlates} onChange={v => handleChange('numberOfFlatPlates', v)} type="number" />
              <Input label="Flap (mm)" value={form.flap} onChange={v => handleChange('flap', v)} type="number" />
            </div>
            <div className="mt-3">
              <Label>Other Specifications</Label>
              <input type="text" value={form.otherSpec} onChange={e => handleChange('otherSpec', e.target.value)}
                className={inputClass} placeholder="Additional specs..." />
            </div>
          </SectionCard>

          {/* ─── SECTION 3: Pin / Joining ─── */}
          <SectionCard title="🔗 Pin & Joining Method" step={3}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label>Pin Angle</Label>
                <select value={form.pinAngle} onChange={e => handleChange('pinAngle', e.target.value)} className={inputClass}>
                  <option value="">Select</option>
                  {PIN_ANGLES.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <Label>Joining Method</Label>
                <select value={form.joiningMethod} onChange={e => handleChange('joiningMethod', e.target.value)} className={inputClass}>
                  <option value="">Select</option>
                  {JOINING_METHODS.map(j => <option key={j.code} value={j.code}>{j.label}</option>)}
                </select>
              </div>
              {form.joiningMethod === 'Metal Hinge' && (
                <div>
                  <Label>Hinge Type</Label>
                  <select value={form.hingeType} onChange={e => handleChange('hingeType', e.target.value)} className={inputClass}>
                    <option value="">Select</option>
                    {HINGE_TYPES.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              )}
            </div>
          </SectionCard>

          {/* ─── SECTION 4: Lag Details ─── */}
          <SectionCard title="📏 Lag Dimensions" step={4}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Input label="Lag Height (mm)" value={form.lagHeight} onChange={v => handleChange('lagHeight', v)} type="number" />
              <Input label="Lag Width (mm)" value={form.lagWidth} onChange={v => handleChange('lagWidth', v)} type="number" />
              <Input label="Number of Lags" value={form.numberOfLags} onChange={v => handleChange('numberOfLags', v)} type="number" />
            </div>
            {effectiveLength > 0 && (
              <div className="mt-3 p-3 bg-blue-50 rounded-xl border border-blue-200 text-xs font-bold text-blue-800">
                Effective Length: {effectiveLength} mm ({form.insideClearance ? 'I/C' : 'O/C'} = {Math.ceil((Number(form.lagHeight) || 0) * 8.33)} mm)
              </div>
            )}
          </SectionCard>

          {/* ─── OPTIONAL SECTIONS ─── */}
          <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6 space-y-4">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3">
              Optional Sections
            </h2>
            <div className="flex flex-wrap gap-2">
              <CheckToggle label="Belt Details" checked={form.enableBelt} onChange={() => handleChange('enableBelt', !form.enableBelt)} />
              <CheckToggle label="Adjustable Position" checked={form.enableAP} onChange={() => handleChange('enableAP', !form.enableAP)} />
              <CheckToggle label="Back Channel" checked={form.enableBAC} onChange={() => handleChange('enableBAC', !form.enableBAC)} />
              <CheckToggle label="Spokes" checked={form.enableSpokes} onChange={() => handleChange('enableSpokes', !form.enableSpokes)} />
              <CheckToggle label="Rivets" checked={form.enableRivets} onChange={() => handleChange('enableRivets', !form.enableRivets)} />
            </div>

            {/* Belt Details */}
            {form.enableBelt && (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3 animate-fadeIn">
                <h3 className="text-xs font-bold text-slate-700 uppercase">Belt Configuration</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <Label>Belt Type</Label>
                    <select value={form.beltType} onChange={e => handleChange('beltType', e.target.value)} className={inputClass}>
                      <option value="">Select</option>
                      {BELT_TYPES.map(b => <option key={b.code} value={b.code}>{b.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Belt Pattern</Label>
                    <select value={form.beltPattern} onChange={e => handleChange('beltPattern', e.target.value)} className={inputClass}>
                      <option value="">Select</option>
                      {form.beltType && <option value={form.beltType}>{form.beltType}</option>}
                    </select>
                  </div>
                  <Input label="Distance Between Supports" value={form.distanceBetweenSupports} onChange={v => handleChange('distanceBetweenSupports', v)} type="number" />
                  <Input label="Spacing (mm)" value={form.beltSpacing} onChange={v => handleChange('beltSpacing', v)} type="number" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <Label>Total Number of Belts</Label>
                    <select value={form.numberOfBelts} onChange={e => handleChange('numberOfBelts', e.target.value)} className={inputClass}>
                      <option value="">Select</option>
                      {NUMBER_OF_BELTS_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Belt Width</Label>
                    <select value={form.beltWidth} onChange={e => handleChange('beltWidth', e.target.value)} className={inputClass}>
                      <option value="">Select</option>
                      {BELT_WIDTHS.map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Adjustable Position */}
            {form.enableAP && (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3 animate-fadeIn">
                <h3 className="text-xs font-bold text-slate-700 uppercase">Adjustable Position</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Position</Label>
                    <select value={form.apPosition} onChange={e => handleChange('apPosition', e.target.value)} className={inputClass}>
                      <option value="">Select</option>
                      <option value="Left">Left</option>
                      <option value="Right">Right</option>
                      <option value="Center">Center</option>
                    </select>
                  </div>
                  <div>
                    <Label>Thickness</Label>
                    <select value={form.apThickness} onChange={e => handleChange('apThickness', e.target.value)} className={inputClass}>
                      <option value="">Select</option>
                      <option value="2mm">2mm</option>
                      <option value="3mm">3mm</option>
                      <option value="4mm">4mm</option>
                      <option value="5mm">5mm</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Back Channel */}
            {form.enableBAC && (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3 animate-fadeIn">
                <h3 className="text-xs font-bold text-slate-700 uppercase">Back Channel / Support</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <select value={form.backChannelType} onChange={e => handleChange('backChannelType', e.target.value)} className={inputClass}>
                      <option value="">Select</option>
                      {BACK_CHANNEL_TYPES.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <CheckToggle label="Strap Support" checked={form.strapSupport} onChange={() => handleChange('strapSupport', !form.strapSupport)} />
                  </div>
                </div>
              </div>
            )}

            {/* Spokes */}
            {form.enableSpokes && (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3 animate-fadeIn">
                <h3 className="text-xs font-bold text-slate-700 uppercase">Spoke Details</h3>
                <div>
                  <Label>Spoke Type</Label>
                  <select value={form.spokeList} onChange={e => handleChange('spokeList', e.target.value)} className={inputClass}>
                    <option value="">Select</option>
                    {SPOKE_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* Rivets */}
            {form.enableRivets && (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3 animate-fadeIn">
                <h3 className="text-xs font-bold text-slate-700 uppercase">Rivet / Tivot Details</h3>
                <div>
                  <Label>Size</Label>
                  <select value={form.rivetSize} onChange={e => handleChange('rivetSize', e.target.value)} className={inputClass}>
                    <option value="">Select</option>
                    {RIVET_SIZES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* ─── SECTION 5: Company / Party ─── */}
          <SectionCard title="🏢 Company & Party" step={5}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Company</Label>
                <select value={form.companyId} onChange={e => handleChange('companyId', e.target.value)} className={inputClass}>
                  <option value="">Select Company</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                </select>
              </div>
              <Input label="Sales Rep ID" value={form.repId} onChange={v => handleChange('repId', v)} type="number" />
              <Input label="Party ID" value={form.partyId} onChange={v => handleChange('partyId', v)} type="number" />
            </div>
          </SectionCard>
        </div>

        {/* ─── RIGHT: Live Preview ─── */}
        <div className="space-y-6">
          {/* Data Summary */}
          <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6 space-y-3 sticky top-4">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3">
              📋 Data Summary
            </h2>
            {latticeData.length > 0 ? (
              <div className="space-y-2">
                {latticeData.map(d => (
                  <div key={d.label} className="flex justify-between text-xs">
                    <span className="text-slate-500 font-medium">{d.label}</span>
                    <span className="font-bold text-slate-900">{d.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-6">
                Fill in the form to see the lattice data preview.
              </p>
            )}

            {latticeData.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <div className="flex justify-between text-sm font-black">
                  <span className="text-slate-700">Total Fields</span>
                  <span className="text-blue-700">{latticeData.length}</span>
                </div>
                {effectiveLength > 0 && (
                  <div className="flex justify-between text-sm font-black mt-1">
                    <span className="text-slate-700">Effective Length</span>
                    <span className="text-blue-700">{effectiveLength} mm</span>
                  </div>
                )}
              </div>
            )}

            {/* Active Sections */}
            <div className="pt-3 border-t border-slate-100">
              <h3 className="text-[10px] font-bold text-slate-600 uppercase mb-2">Active Sections</h3>
              <div className="flex flex-wrap gap-1">
                {['Basic', 'Frame', 'Joining', 'Lags', 'Company'].map(s => (
                  <span key={s} className="text-[9px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-bold">{s}</span>
                ))}
                {form.enableBelt && <span className="text-[9px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-bold">Belt</span>}
                {form.enableAP && <span className="text-[9px] px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full font-bold">AP</span>}
                {form.enableBAC && <span className="text-[9px] px-2 py-0.5 bg-orange-50 text-orange-700 rounded-full font-bold">BAC</span>}
                {form.enableSpokes && <span className="text-[9px] px-2 py-0.5 bg-cyan-50 text-cyan-700 rounded-full font-bold">Spokes</span>}
                {form.enableRivets && <span className="text-[9px] px-2 py-0.5 bg-red-50 text-red-700 rounded-full font-bold">Rivets</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Helper Components ─── */

function SectionCard({ title, step, children }: { title: string; step: number; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6 space-y-4">
      <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
        <div className="h-7 w-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-black">{step}</div>
        <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">{children}</label>;
}

function Input({ label, value, onChange, type = 'text', required }: {
  label: string; value: any; onChange: (v: any) => void; type?: string; required?: boolean;
}) {
  return (
    <div>
      <Label>{label}{required && ' *'}</Label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
        className={inputClass}
        required={required}
      />
    </div>
  );
}

function CheckToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <button onClick={onChange} className={`px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all cursor-pointer ${
      checked ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-blue-300 text-slate-600'
    }`}>
      {checked ? '✓' : '○'} {label}
    </button>
  );
}

const inputClass = 'block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white';
